import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  qontoService,
  syncService,
  unpaidService,
  recurringService,
  accountingService,
} from '@/services/qonto'
import type {
  TransactionFilters,
  UnpaidFilters,
  RecurringContractFilters,
  RecurringContractFormData,
  UnpaidAlertThresholds,
} from '@/types/qonto'

// ============================================
// STORY-039: Hooks API Qonto
// ============================================

export function useQontoConnection() {
  return useQuery({
    queryKey: ['qonto-connection'],
    queryFn: () => qontoService.testConnection(),
    staleTime: 5 * 60 * 1000, // 5 minutes
  })
}

export function useQontoBankAccounts() {
  const { data: isConnected } = useQontoConnection()

  return useQuery({
    queryKey: ['qonto-accounts'],
    queryFn: () => qontoService.getBankAccounts(),
    enabled: !!isConnected,
    staleTime: 5 * 60 * 1000,
    retry: 1,
  })
}

export function useSaveQontoCredentials() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (credentials: { login: string; secretKey: string }) =>
      qontoService.saveCredentials(credentials),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['qonto-connection'] })
      queryClient.invalidateQueries({ queryKey: ['qonto-accounts'] })
    },
  })
}

export function useRemoveQontoCredentials() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: () => qontoService.removeCredentials(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['qonto-connection'] })
      queryClient.invalidateQueries({ queryKey: ['qonto-accounts'] })
    },
  })
}

// ============================================
// STORY-040: Hooks Synchronisation
// ============================================

export function useSyncedTransactions(filters?: TransactionFilters) {
  return useQuery({
    queryKey: ['synced-transactions', filters],
    queryFn: () => syncService.getTransactions(filters),
  })
}

export function useSyncLogs(limit = 10) {
  return useQuery({
    queryKey: ['sync-logs', limit],
    queryFn: () => syncService.getSyncLogs(limit),
  })
}

export function useQontoStats() {
  return useQuery({
    queryKey: ['qonto-stats'],
    queryFn: () => syncService.getStats(),
    staleTime: 60 * 1000, // 1 minute
  })
}

export function useStartSync() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      accountSlug,
      dateFrom,
      dateTo,
    }: {
      accountSlug: string
      dateFrom?: string
      dateTo?: string
    }) => syncService.startSync(accountSlug, dateFrom, dateTo),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['synced-transactions'] })
      queryClient.invalidateQueries({ queryKey: ['sync-logs'] })
      queryClient.invalidateQueries({ queryKey: ['qonto-stats'] })
    },
  })
}

export function useManualMatch() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      transactionId,
      invoiceId,
    }: {
      transactionId: string
      invoiceId: string
    }) => syncService.manualMatch(transactionId, invoiceId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['synced-transactions'] })
      queryClient.invalidateQueries({ queryKey: ['invoices'] })
      queryClient.invalidateQueries({ queryKey: ['qonto-stats'] })
    },
  })
}

export function useIgnoreTransaction() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (transactionId: string) => syncService.ignoreTransaction(transactionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['synced-transactions'] })
    },
  })
}

export function useUnmatchTransaction() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (transactionId: string) => syncService.unmatch(transactionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['synced-transactions'] })
      queryClient.invalidateQueries({ queryKey: ['invoices'] })
      queryClient.invalidateQueries({ queryKey: ['qonto-stats'] })
    },
  })
}

// ============================================
// STORY-041: Hooks Detection Impayes
// ============================================

export function useUnpaidAlerts(filters?: UnpaidFilters) {
  return useQuery({
    queryKey: ['unpaid-alerts', filters],
    queryFn: () => unpaidService.getAlerts(filters),
  })
}

export function useDetectUnpaid() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (thresholds?: Partial<UnpaidAlertThresholds>) =>
      unpaidService.detectUnpaid(thresholds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['unpaid-alerts'] })
      queryClient.invalidateQueries({ queryKey: ['invoices'] })
      queryClient.invalidateQueries({ queryKey: ['accounting-stats'] })
    },
  })
}

export function useAcknowledgeAlert() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (alertId: string) => unpaidService.acknowledgeAlert(alertId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['unpaid-alerts'] })
    },
  })
}

export function useResolveUnpaidAlert() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (alertId: string) => unpaidService.resolveAlert(alertId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['unpaid-alerts'] })
      queryClient.invalidateQueries({ queryKey: ['invoices'] })
      queryClient.invalidateQueries({ queryKey: ['accounting-stats'] })
    },
  })
}

export function useEscalateUnpaidAlert() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ alertId, notes }: { alertId: string; notes?: string }) =>
      unpaidService.escalateAlert(alertId, notes),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['unpaid-alerts'] })
    },
  })
}

export function useSendUnpaidReminder() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (alertId: string) => unpaidService.sendReminder(alertId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['unpaid-alerts'] })
    },
  })
}

export function useUnpaidThresholds() {
  return useQuery({
    queryKey: ['unpaid-thresholds'],
    queryFn: () => unpaidService.getThresholds(),
  })
}

export function useSaveUnpaidThresholds() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (thresholds: UnpaidAlertThresholds) =>
      unpaidService.saveThresholds(thresholds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['unpaid-thresholds'] })
    },
  })
}

// ============================================
// STORY-042: Hooks Paiements Recurrents
// ============================================

export function useRecurringContracts(filters?: RecurringContractFilters) {
  return useQuery({
    queryKey: ['recurring-contracts', filters],
    queryFn: () => recurringService.getContracts(filters),
  })
}

export function useRecurringContract(id: string) {
  return useQuery({
    queryKey: ['recurring-contract', id],
    queryFn: () => recurringService.getById(id),
    enabled: !!id,
  })
}

export function useCreateRecurringContract() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: RecurringContractFormData) => recurringService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['recurring-contracts'] })
      queryClient.invalidateQueries({ queryKey: ['recurring-stats'] })
    },
  })
}

export function useUpdateRecurringContract() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<RecurringContractFormData> }) =>
      recurringService.update(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['recurring-contract', id] })
      queryClient.invalidateQueries({ queryKey: ['recurring-contracts'] })
      queryClient.invalidateQueries({ queryKey: ['recurring-stats'] })
    },
  })
}

export function useUpdateRecurringContractStatus() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: 'active' | 'paused' | 'cancelled' | 'completed' }) =>
      recurringService.updateStatus(id, status),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['recurring-contract', id] })
      queryClient.invalidateQueries({ queryKey: ['recurring-contracts'] })
      queryClient.invalidateQueries({ queryKey: ['recurring-stats'] })
    },
  })
}

export function useDeleteRecurringContract() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => recurringService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['recurring-contracts'] })
      queryClient.invalidateQueries({ queryKey: ['recurring-stats'] })
    },
  })
}

export function useGenerateRecurringInvoice() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (contractId: string) => recurringService.generateInvoice(contractId),
    onSuccess: (_, contractId) => {
      queryClient.invalidateQueries({ queryKey: ['recurring-contract', contractId] })
      queryClient.invalidateQueries({ queryKey: ['recurring-contracts'] })
      queryClient.invalidateQueries({ queryKey: ['invoices'] })
      queryClient.invalidateQueries({ queryKey: ['invoices-stats'] })
    },
  })
}

export function useGenerateDueInvoices() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: () => recurringService.generateDueInvoices(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['recurring-contracts'] })
      queryClient.invalidateQueries({ queryKey: ['invoices'] })
      queryClient.invalidateQueries({ queryKey: ['invoices-stats'] })
      queryClient.invalidateQueries({ queryKey: ['recurring-stats'] })
    },
  })
}

export function useRecurringInvoiceHistory(contractId: string) {
  return useQuery({
    queryKey: ['recurring-invoice-history', contractId],
    queryFn: () => recurringService.getInvoiceHistory(contractId),
    enabled: !!contractId,
  })
}

export function useRecurringStats() {
  return useQuery({
    queryKey: ['recurring-stats'],
    queryFn: () => recurringService.getStats(),
    staleTime: 60 * 1000,
  })
}

// ============================================
// Statistiques Globales Comptabilite
// ============================================

export function useAccountingStats() {
  return useQuery({
    queryKey: ['accounting-stats'],
    queryFn: () => accountingService.getStats(),
    staleTime: 60 * 1000,
  })
}
